import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { 
  Video, Users, Calendar, FileText, MessageSquare, CreditCard,
  Brain, Shield, Clock, Bell, Search, Plus, ChevronRight,
  Phone, Mail, CheckCircle, AlertCircle, TrendingUp, Heart,
  Settings, HelpCircle, Star, Activity
} from 'lucide-react';

const BehavioralHealthDashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');

  const stats = [
    { label: 'Active Clients', value: '47', icon: Users, color: 'bg-blue-500', change: '+5 this month' },
    { label: 'Sessions This Week', value: '23', icon: Video, color: 'bg-green-500', change: '8 remaining' },
    { label: 'Pending Notes', value: '4', icon: FileText, color: 'bg-orange-500', change: 'Due today' },
    { label: 'Unread Messages', value: '12', icon: MessageSquare, color: 'bg-purple-500', change: '3 urgent' },
  ];

  const upcomingSessions = [
    { id: 1, client: 'Sarah Johnson', time: '9:00 AM', type: 'Individual Therapy', status: 'confirmed', avatar: 'SJ' },
    { id: 2, client: 'Michael Chen', time: '10:30 AM', type: 'Couples Counseling', status: 'confirmed', avatar: 'MC' },
    { id: 3, client: 'Emily Rodriguez', time: '1:00 PM', type: 'CBT Session', status: 'pending', avatar: 'ER' },
    { id: 4, client: 'David Thompson', time: '3:00 PM', type: 'Initial Assessment', status: 'confirmed', avatar: 'DT' },
  ];

  const recentClients = [
    { id: 1, name: 'Sarah Johnson', lastSession: '2 days ago', nextSession: 'Today 9:00 AM', status: 'active' },
    { id: 2, name: 'Michael Chen', lastSession: '1 week ago', nextSession: 'Today 10:30 AM', status: 'active' },
    { id: 3, name: 'Emily Rodriguez', lastSession: '3 days ago', nextSession: 'Today 1:00 PM', status: 'active' },
    { id: 4, name: 'James Wilson', lastSession: '2 weeks ago', nextSession: 'Tomorrow 2:00 PM', status: 'inactive' },
  ];

  const quickActions = [
    { name: 'Start Video Session', icon: Video, href: '/dashboard/behavioral-health/telehealth', color: 'bg-green-500' },
    { name: 'New Client', icon: Plus, href: '/dashboard/behavioral-health/clients/new', color: 'bg-blue-500' },
    { name: 'Schedule Session', icon: Calendar, href: '/dashboard/behavioral-health/schedule', color: 'bg-purple-500' },
    { name: 'Write Note', icon: FileText, href: '/dashboard/behavioral-health/notes', color: 'bg-orange-500' },
  ];

  return (
    <DashboardLayout>
      <div className="p-6 bg-gray-50 min-h-screen">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <Brain className="w-8 h-8 text-orange-500" />
                Behavioral Health Center
              </h1>
              <p className="text-gray-500 mt-1">HIPAA Compliant Telehealth & Practice Management</p>
            </div>
            <div className="flex items-center gap-3">
              <span className="flex items-center gap-2 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm">
                <Shield className="w-4 h-4" />
                HIPAA Compliant
              </span>
              <button className="p-2 bg-white rounded-lg shadow hover:shadow-md transition-shadow">
                <Bell className="w-5 h-5 text-gray-600" />
              </button>
              <button className="p-2 bg-white rounded-lg shadow hover:shadow-md transition-shadow">
                <Settings className="w-5 h-5 text-gray-600" />
              </button>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-4 gap-4 mb-8">
          {quickActions.map((action, idx) => (
            <Link
              key={idx}
              to={action.href}
              className="flex items-center gap-4 p-4 bg-white rounded-xl shadow hover:shadow-lg transition-all hover:scale-[1.02]"
            >
              <div className={`${action.color} p-3 rounded-lg`}>
                <action.icon className="w-6 h-6 text-white" />
              </div>
              <span className="font-medium text-gray-800">{action.name}</span>
              <ChevronRight className="w-5 h-5 text-gray-400 ml-auto" />
            </Link>
          ))}
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-4 gap-6 mb-8">
          {stats.map((stat, idx) => (
            <div key={idx} className="bg-white rounded-xl shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <div className={`${stat.color} p-3 rounded-lg`}>
                  <stat.icon className="w-6 h-6 text-white" />
                </div>
                <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">{stat.change}</span>
              </div>
              <h3 className="text-3xl font-bold text-gray-900">{stat.value}</h3>
              <p className="text-gray-500 text-sm">{stat.label}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-3 gap-6">
          {/* Today's Schedule */}
          <div className="col-span-2 bg-white rounded-xl shadow">
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-orange-500" />
                  Today's Sessions
                </h2>
                <Link to="/dashboard/behavioral-health/schedule" className="text-orange-500 text-sm hover:underline">
                  View Full Schedule
                </Link>
              </div>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {upcomingSessions.map((session) => (
                  <div key={session.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center text-orange-600 font-bold">
                        {session.avatar}
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">{session.client}</h4>
                        <p className="text-sm text-gray-500">{session.type}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="font-medium text-gray-900">{session.time}</p>
                        <span className={`text-xs px-2 py-1 rounded ${
                          session.status === 'confirmed' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                        }`}>
                          {session.status}
                        </span>
                      </div>
                      <Link 
                        to="/dashboard/behavioral-health/telehealth"
                        className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors flex items-center gap-2"
                      >
                        <Video className="w-4 h-4" />
                        Start
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Recent Clients */}
          <div className="bg-white rounded-xl shadow">
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <Users className="w-5 h-5 text-orange-500" />
                  Recent Clients
                </h2>
                <Link to="/dashboard/behavioral-health/clients" className="text-orange-500 text-sm hover:underline">
                  View All
                </Link>
              </div>
            </div>
            <div className="p-4">
              <div className="space-y-3">
                {recentClients.map((client) => (
                  <Link 
                    key={client.id}
                    to={`/dashboard/behavioral-health/clients/${client.id}`}
                    className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-2 h-2 rounded-full ${client.status === 'active' ? 'bg-green-500' : 'bg-gray-400'}`} />
                      <div>
                        <h4 className="font-medium text-gray-900 text-sm">{client.name}</h4>
                        <p className="text-xs text-gray-500">Next: {client.nextSession}</p>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Feature Cards */}
        <div className="grid grid-cols-3 gap-6 mt-8">
          <Link to="/dashboard/behavioral-health/telehealth" className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white hover:shadow-lg transition-shadow">
            <Video className="w-10 h-10 mb-4" />
            <h3 className="text-xl font-bold mb-2">Telehealth Sessions</h3>
            <p className="text-green-100 text-sm">Secure, HIPAA-compliant video sessions with your clients</p>
          </Link>
          
          <Link to="/dashboard/behavioral-health/notes" className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white hover:shadow-lg transition-shadow">
            <FileText className="w-10 h-10 mb-4" />
            <h3 className="text-xl font-bold mb-2">Session Notes</h3>
            <p className="text-blue-100 text-sm">Document sessions with templates and AI assistance</p>
          </Link>
          
          <Link to="/dashboard/behavioral-health/messages" className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-6 text-white hover:shadow-lg transition-shadow">
            <MessageSquare className="w-10 h-10 mb-4" />
            <h3 className="text-xl font-bold mb-2">Secure Messaging</h3>
            <p className="text-purple-100 text-sm">Encrypted communication with clients between sessions</p>
          </Link>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default BehavioralHealthDashboard;
